import { addMinutes, parseISO, subMinutes } from 'date-fns'
import { Check, Copy } from 'lucide-react'
import { use, useMemo, useState } from 'react'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { LogFilterProviderContext } from '@/components/shared/log-filters/context/context'
import { JsonPropertyViewer } from '@/components/shared/logs-explorer/components/json-property-viewer'
import { LogsExplorerContext } from '@/components/shared/logs-explorer/context/context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getIntervalInSeconds } from '@/config/charts'
import type { MetricsOperator } from '@/graphql.types'

type Props = {
  idLog?: string
  severity?: string
  timestamp?: string
  attributes?: Record<string, unknown> | null
  tags?: Record<string, unknown> | null
}

const deepParseJson = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return deepParseJson(JSON.parse(trimmed))
      } catch {
        return value
      }
    }
    return value
  }

  if (Array.isArray(value)) {
    return value.map(deepParseJson)
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        deepParseJson(v),
      ])
    )
  }

  return value
}

const filterObject = (
  obj: Record<string, unknown>,
  searchTerm: string
): Record<string, unknown> | null => {
  if (!searchTerm) {
    return obj
  }

  const lowerSearch = searchTerm.toLowerCase()
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    if (key.toLowerCase().includes(lowerSearch)) {
      result[key] = value
      continue
    }

    if (
      typeof value === 'string' &&
      value.toLowerCase().includes(lowerSearch)
    ) {
      result[key] = value
      continue
    }

    if (typeof value === 'number' && String(value).includes(searchTerm)) {
      result[key] = value
      continue
    }

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const filtered = filterObject(
        value as Record<string, unknown>,
        searchTerm
      )
      if (filtered && Object.keys(filtered).length > 0) {
        result[key] = filtered
      }
    }

    if (Array.isArray(value)) {
      const filteredArray = value.filter((item) => {
        if (typeof item === 'string') {
          return item.toLowerCase().includes(lowerSearch)
        }
        if (typeof item === 'number') {
          return String(item).includes(searchTerm)
        }
        if (item !== null && typeof item === 'object') {
          const filtered = filterObject(
            item as Record<string, unknown>,
            searchTerm
          )
          return filtered && Object.keys(filtered).length > 0
        }
        return false
      })
      if (filteredArray.length > 0) {
        result[key] = filteredArray
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null
}

export const LogAttributes = ({
  idLog,
  severity,
  timestamp,
  attributes,
  tags,
}: Props) => {
  const logsExplorerContext = use(LogsExplorerContext)
  const { filters, setFilters } = use(LogFilterProviderContext)
  const { setTimeframe } = use(ChartTimeframeProviderContext)

  const isFilteringDisabled = logsExplorerContext?.config.isFilteringDisabled
  const [searchTerm, setSearchTerm] = useState('')
  const [isCopied, setIsCopied] = useState(false)

  const handleSetTimeframe = (minutes: number) => {
    if (!timestamp) {
      return
    }
    const logDate = parseISO(timestamp.substring(0, 23) + 'Z')
    const startDate = subMinutes(logDate, minutes)
    const endDate = addMinutes(logDate, minutes)
    setTimeframe({
      startDate,
      endDate,
      intervalInSeconds: getIntervalInSeconds(startDate, endDate),
    })
  }

  const handleAddFilter = ({
    key,
    value,
    operator,
  }: {
    key: string
    value: string
    operator: MetricsOperator
  }) => {
    const isDuplicate = filters.some(
      (f) => f.key === key && f.operator === operator && f.value === value
    )
    if (isDuplicate) {
      return
    }
    setFilters([...filters, { key, operator, value }])
  }

  const src = useMemo(() => {
    const parsed = {
      id: idLog ?? '',
      severity: severity ?? '',
      attributes:
        attributes && Object.keys(attributes).length > 0
          ? deepParseJson(attributes)
          : {},
      tags: tags && Object.keys(tags).length > 0 ? deepParseJson(tags) : {},
    }

    if (!searchTerm) {
      return parsed
    }

    const lowerSearch = searchTerm.toLowerCase()
    const idMatches =
      idLog && idLog.toLowerCase().includes(lowerSearch) ? idLog : null
    const severityMatches =
      severity && severity.toLowerCase().includes(lowerSearch) ? severity : null

    const filteredAttributes = filterObject(
      parsed.attributes as Record<string, unknown>,
      searchTerm
    )
    const filteredTags = filterObject(
      parsed.tags as Record<string, unknown>,
      searchTerm
    )

    return {
      ...(idMatches ? { id: idMatches } : {}),
      ...(severityMatches ? { severity: severityMatches } : {}),
      attributes: filteredAttributes ?? {},
      tags: filteredTags ?? {},
    }
  }, [idLog, severity, attributes, tags, searchTerm])

  const hasResults =
    ('id' in src && src.id) ||
    ('severity' in src && src.severity) ||
    Object.keys(src.attributes as object).length > 0 ||
    Object.keys(src.tags as object).length > 0

  const handleCopy = () => {
    const fullData = {
      id: idLog ?? '',
      severity: severity ?? '',
      attributes:
        attributes && Object.keys(attributes).length > 0
          ? deepParseJson(attributes)
          : {},
      tags: tags && Object.keys(tags).length > 0 ? deepParseJson(tags) : {},
    }
    navigator.clipboard.writeText(JSON.stringify(fullData, null, 2)).then(
      () => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
      },
      () => setIsCopied(false)
    )
  }

  return (
    <div className="mt-1.5 mb-2 !w-full">
      <div className="flex justify-between items-center w-full mb-2">
        <Input
          type="text"
          placeholder="Search properties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-7 w-52 text-xs bg-background font-sans"
        />
        <div className="flex items-center gap-1.5">
          {timestamp && !isFilteringDisabled && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSetTimeframe(1)}
                className="h-7 px-2 text-xs font-sans"
              >
                ±1m
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSetTimeframe(5)}
                className="h-7 px-2 text-xs font-sans"
              >
                ±5m
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSetTimeframe(10)}
                className="h-7 px-2 text-xs font-sans"
              >
                ±10m
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            className="h-7 px-2 text-xs font-sans"
          >
            {isCopied ? (
              <Check className="size-3.5 mr-1" />
            ) : (
              <Copy className="size-3.5 mr-1" />
            )}
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>
      <div className="rounded-md border border-border/60 bg-muted p-2 px-4">
        {hasResults ? (
          <JsonPropertyViewer
            data={src}
            onAddFilter={isFilteringDisabled ? undefined : handleAddFilter}
            searchTerm={searchTerm}
          />
        ) : (
          <div className="text-xs text-muted-foreground py-2">
            No matching properties found
          </div>
        )}
      </div>
    </div>
  )
}
